/**
 * Which paths are on the customer plane, and the session probe
 * `CustomerAuthProvider` uses to tell an existing session from none.
 *
 * Authority: bead scope-r3x5; PRD §4.1–§4.2. Path shapes fixed by scope-5fos.
 *
 * There is no dedicated "who am I" endpoint for a customer session, unlike
 * staff — MSAL holds the account and never needs to ask the server who it is.
 * `GET .../classification` is a side-effect-free read
 * (`apps/api/src/functions/classification.ts`'s doc comment: "a plain read...
 * it does not evaluate a new run") that 401s with no session cookie and 200s
 * with a live one, so this borrows it as the probe rather than adding a
 * second endpoint whose only job would be answering the same question.
 *
 * **Path shape.** The Functions host (default `routePrefix`, no rewrite in
 * `staticwebapp.config.json`) actually serves these under
 * `/api/v1/engagements/{id}/...`, not `/api/customer/engagements/...` — the
 * two path shapes were never reconciled when the customer plane's fetch
 * paths were first written (scope-5fos). `{id}` is a literal path segment
 * `withCustomerSession` checks against the session when it parses as a UUID
 * (`packages/api-auth/src/path.ts`) and otherwise ignores
 * (`assertPathMatchesSession` in `with-session.ts` treats a non-UUID segment
 * the same as no id at all) — a customer screen never learns its real
 * engagement id (see `customerAnswers.ts`'s doc comment), so `self` is used
 * as that segment rather than a UUID this code does not have.
 */
import { matchRoute } from '../routes/_registry/match.ts'
import { activeRegistry } from '../routes/_registry/registry.active.ts'
import type { RouteRegistry } from '../routes/_registry/types.ts'

/**
 * Is this path on the customer plane?
 *
 * Asked of the registry rather than the URL, exactly like
 * `msal-session.ts`'s `isStaffPlanePath` — a gate that reads a prefix out of
 * the path is a gate a renamed route turns off in silence.
 */
export function isCustomerPlanePath(
  pathname: string,
  routes: RouteRegistry = activeRegistry(),
): boolean {
  return matchRoute(routes, pathname)?.route.plane === 'customer'
}

export const CUSTOMER_CLASSIFICATION_PATH = '/api/v1/engagements/self/classification'
export const CUSTOMER_ANSWERS_PATH = '/api/v1/engagements/self/answers'
export const CUSTOMER_QUESTIONS_PATH = '/api/v1/engagements/self/questions'
export const CUSTOMER_SUBMIT_PATH = '/api/v1/engagements/self/submit'

export const CUSTOMER_ME_PATH = '/api/v1/me/engagements'

/**
 * Three states, not two (IMPL-ENGAGE-002 §8.2).
 *
 * `choosing` is the verified-but-unbound state the person tier introduces: a
 * code has been verified, but the cookie holds a short-lived SELECTION token
 * rather than an engagement-bound session, because the address reaches more
 * than one engagement and has not picked yet.
 *
 * Without this the probe reads that state as `signed-out` — `/classification`
 * needs a real engagement claim and 401s under a selection token — so a
 * refresh anywhere on the chooser would throw the person back to the OTP form
 * with a perfectly good credential in hand.
 */
export type CustomerSessionProbe = 'signed-in' | 'choosing' | 'signed-out'

/**
 * Probes for an existing, live session cookie.
 *
 * ┌─ A 404 HERE MEANS "NOT CLASSIFIED", NOT "NOT SIGNED IN". ────────────────┐
 * │ `readClassification` throws `notFound('this engagement has not been      │
 * │ classified yet')` when no run exists — which is the state of EVERY new   │
 * │ engagement, because the first run is triggered by answering questions.   │
 * │                                                                          │
 * │ This probe read that as "not bound", fell through to `/me/*`, got a 200  │
 * │ from the perfectly good session, and answered `choosing` — so            │
 * │ `ChoosingGate` sent the owner back to the engagement list. Every owner   │
 * │ entering a brand-new engagement bounced, and could only stop bouncing by │
 * │ answering the questions they were being kept out of.                     │
 * │                                                                          │
 * │ Both no-credential outcomes are 401 (`unauthenticated` and               │
 * │ `sessionExpired` alike), so the two cases never overlap.                 │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * Anything else — a 503, a network failure — reads as `signed-out`, back to
 * the OTP form rather than a blank screen or a thrown error a caller has to
 * guess how to render. `credentials: 'same-origin'` is the fetch default; it
 * is named explicitly so a reader does not have to already know that.
 */
export async function probeCustomerSession(
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<CustomerSessionProbe> {
  try {
    const bound = await fetchImpl(CUSTOMER_CLASSIFICATION_PATH, { credentials: 'same-origin' })
    // 200: bound, and classified. 404: bound, and simply not classified yet.
    // Both mean the cookie names an engagement, which is what this asks.
    if (bound.ok || bound.status === NOT_CLASSIFIED_YET) return 'signed-in'

    /*
     * 401. Possibly still verified: `/me/*` reads the address off the session
     * and answers for any live one, so a 200 here means "verified, has not
     * chosen". Asked second so the common case still costs one request.
     */
    const verified = await fetchImpl(CUSTOMER_ME_PATH, { credentials: 'same-origin' })

    return verified.ok ? 'choosing' : 'signed-out'
  } catch {
    return 'signed-out'
  }
}

/** `notFound()` from `readClassification`; never an authentication outcome. */
const NOT_CLASSIFIED_YET = 404
